import { useCallback, useState } from "react";
import type { ThunkAction, UnknownAction } from "@reduxjs/toolkit";
import { useAppDispatch } from "@/shared/hooks/redux-hooks";
import type { RootState } from "@/shared/lib/store/store";
import {
  DebouncedMutationOptions,
  RtkQueryMutation,
  useDebouncedMutation,
} from "@/shared/lib/debounce/debounce";

/**
 * CacheUpdate is a function updating the RKT Query cache with the passed
 * value. The implementation will typically look like this:
 *
 * @example
 * ```
 * const updateEntityCache = (entity: EntityType) =>
 *   entityApi.util.updateQueryData(
 *     "showEntity", // the name of the query endpoint to fetch the entity
 *     id: entity.id,
 *     () => entity,
 *   );
 * ```
 */
type CacheUpdate<T> = (
  value: T,
) => ThunkAction<unknown, RootState, unknown, UnknownAction>;

/**
 * The cache-value argument of the returned setter. Optional when the request
 * *is* a full cache value (PUT: `Req` assignable to `CacheValue`); required when
 * it isn't (PATCH), so a partial request can never be mistaken for the optimistic
 * cache value. The `[ ]` wrap keeps the check non-distributive over unions.
 */
type CacheArg<Req, CacheValue> = [Req] extends [CacheValue]
  ? [cacheValue?: CacheValue]
  : [cacheValue: CacheValue];

/**
 * useOptimisticDebounceMutation returns a `set(request, cache)` function that:
 *
 *  1. immediately patches the RTK Query cache via `updateCache` for instant UI
 *  2. schedules a debounced mutation through {@link useDebouncedMutation}
 *
 * @experimental - This is not really trivial, usage must be tested at
 * selected sites and see if it leads to issues in real-life usage,
 * before applying it globally (as of 07.2026).
 *
 * Using useOptimisticDebounceMutation allows you to use the RTK
 * Query cache state to render your UI state and get immediate
 * updates on value change. This eliminates the need to intoduce a
 * local `const [value, setValue] = useState()` to control input
 * fields. This guarantees that any cache invalidation will
 * always appear in the UI. - This guarantee can be tricky to
 * keep with local state in combination with debounced input.
 * NOTE: There is one gotcha: Get the RTK query state directly
 * from the hook returning the value and do not pass it down
 * the component tree. The latter may cause a re-render and
 * movement of the cursor to the end of an input field.
 *
 * When using useOptimisticDebounceMutation, you MUST make sure that
 * the mutation invalidates the entity's tag on success and on error,
 * so the server state is refetched:
 *  - on error, this resets the rejected optimistic value, which would
 *    otherwise linger in the cache;
 *  - on success, this picks up server-derived state (a document state,
 *    a number, a computed value) the optimistic value cannot know.
 * See RTK Query documentation (read "Tip" section for debounce ==
 * "triggered in short succession"):
 * https://redux-toolkit.js.org/rtk-query/usage/manual-cache-updates#optimistic-updates
 *
 * The refetch on success does not overwrite input typed in the meantime
 * because three things hold together:
 *  1. All text-like input is collected in debounced leaf components, see
 *     {@link InputField, InputDebounced}. They buffer their own text and
 *     ignore outside values while focused or while a flush is pending.
 *     Discrete controls (select, checkbox, switch, date picker) need no
 *     debouncing: every interaction is one final value.
 *  2. The delay of this hook is 0 (the default), so no edit waits in the
 *     hook's buffer while a refetch lands. With a delay > 0 such a refetch
 *     writes the old value back to the cache and, for a PUT, the next
 *     `set({ ...data, field })` sends it to the server again.
 *  3. RTK Query delays tag invalidation until no query or mutation is
 *     pending (`invalidationBehavior: "delayed"`, the default).
 * A refetch already in flight when a new edit is sent may still briefly
 * show the old value; the invalidation of that edit's mutation heals it.
 *
 * useOptimisicDebounceMutation is extremely simple for PUT mutations
 * where the request is the full entity. Just call `set()` with the
 * full entity.
 * If the request holds only a partial representation, you must pass
 * an (optimistically) correct value, representing the updated entity
 * state as second parmeter. If backend validation fails, this value
 * will then be overwritten. Do not worry about validating this value
 * in the UI. It should be the result as the user input requests it.
 *
 * @param initialRequest The value used to initialise the request send,
 *                       via the mutation. Also the error-reset value
 *                       passed to useDebounceMutation.
 * @param updateCache    A ThunkAction constructor that optimistically
 *                       patches the cache. You may use the `updateCache`
 *                       helper to dynamically construct it.
 * @param mutation       The RTK Query mutation, return of `useFooPutMutation()`
 * @param options        Options passed to {@link DebouncedMutationOptions}.
 *                       Note that the defaults are slightly changed:
 *                       1. The delay is 0. The leaf components debounce;
 *                          keep it at 0, see above.
 *                       2. resetOnError is set to false. The rejected
 *                          value stays in the input, next to the validation
 *                          error the hook writes into the nearest
 *                          ErrValidationFailedContextProvider (see
 *                          `writeToErrValidationFailedContext`). Input components
 *                          given a `fieldName` display it.
 * @returns `set(request, cacheValue)` — dispatches the optimistic cache update
 *          and schedules the mutation. `cacheValue` is required when the request
 *          is only a partial representation (PATCH) and optional when it is the
 *          full entity (PUT).
 */
export function useOptimisticDebouncedMutation<CacheValue, Req, Res>(
  initialRequest: NoInfer<Req>,
  updateCache: CacheUpdate<CacheValue>,
  mutation: RtkQueryMutation<Req, Res>,
  options?: Partial<DebouncedMutationOptions<Req, Res>>,
): [
  (request: Req, ...rest: CacheArg<Req, CacheValue>) => void,
  { error: unknown; isLoading: boolean },
] {
  const dispatch = useAppDispatch();

  // The local buffer is used to drive the debounce.
  const [buffer, setBuffer] = useState<Req>(initialRequest);
  const { error, isLoading } = useDebouncedMutation(
    buffer,
    setBuffer,
    mutation,
    initialRequest,
    { delay: 0, resetOnError: false, ...options },
  );

  return [
    useCallback(
      (request: Req, ...rest: CacheArg<Req, CacheValue>) => {
        // The empty-tuple (no cacheValue) branch is only reachable when `Req` is
        // assignable to `CacheValue`, so falling back to `request` is sound.
        const cacheValue = (rest.length ? rest[0] : request) as CacheValue;
        // Immediate: patch the query cache for instant UI.
        dispatch(updateCache(cacheValue));
        // Debounced: set the buffer, which triggers the
        // mutation through useDebouncedMutation()
        setBuffer(request);
      },
      [dispatch, updateCache],
    ),
    { error, isLoading },
  ];
}
